import { Link } from '@tanstack/react-router';
import { CircleNotchIcon, WarningIcon } from '@phosphor-icons/react';
import { Num, T, Var } from 'gt-react';
import type { AgentRunSummary } from '@erp/rpc';
import { useRunSpans } from 'src/components/agent-trace/useRunSpans';
import { fmtMs, fmtTokens } from 'src/components/agent-trace/utils';
import { deriveTrajectorySummary } from '../../agent-monitor/components/trajectorySummary';
import type {
  TrajectoryInstruction,
  TrajectoryPlan,
  TrajectorySignals,
  TrajectorySummary,
  TrajectoryTask,
} from '../../agent-monitor/components/trajectorySummary';

/**
 * The live run's current task, loaded from the same timeline the activity log renders.
 *
 * Mounted only while a run is in progress. The log below still has the full record; this is the
 * part someone opening the page wants without expanding a row: which task, what is happening
 * now, and the instruction the operator was given.
 */
export function AgentPageLiveTask({ run }: { run: AgentRunSummary }) {
  const { spans, dataUpdatedAt } = useRunSpans(run.id);
  const summary = deriveTrajectorySummary({
    definition: { description: null },
    run,
    spans,
    nowMs: dataUpdatedAt || undefined,
  });
  return <AgentPageCurrentTask summary={summary} />;
}

function taskHeadline(task: TrajectoryTask | null, nowLabel: string | null) {
  return task?.title ?? nowLabel;
}

/** The Now line repeats the headline when the deepest span IS the task phase. */
function nowRepeatsTask(taskTitle: string | null, nowLabel: string): boolean {
  return nowLabel === taskTitle || nowLabel === `Task: ${taskTitle ?? ''}`;
}

/** What the run is doing right now, for a live agent page. */
export function AgentPageCurrentTask({
  summary,
}: {
  summary: TrajectorySummary;
}) {
  const headline = taskHeadline(summary.task, summary.now?.label ?? null);
  const showNow =
    summary.task != null &&
    summary.now != null &&
    !nowRepeatsTask(summary.task.title, summary.now.label);

  return (
    <section className="rounded-lg border border-border bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-stone-500">
            <T context="agent page heading for the task the live run is working">
              Current task
            </T>
          </h2>
          <TaskHeadline task={summary.task} headline={headline} />
        </div>
        <SignalCluster signals={summary.signals} />
      </div>

      {showNow && summary.now && (
        <p className="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs text-foreground">
          <CircleNotchIcon className="h-3.5 w-3.5 shrink-0 animate-spin text-amber-600" />
          <span className="truncate">{summary.now.label}</span>
        </p>
      )}

      {summary.instruction && (
        <InstructionBrief instruction={summary.instruction} />
      )}

      {summary.plan && <PlanLine plan={summary.plan} />}
    </section>
  );
}

function TaskHeadline({
  task,
  headline,
}: {
  task: TrajectoryTask | null;
  headline: string | null;
}) {
  if (!headline) {
    return (
      <p className="mt-0.5 text-sm font-semibold text-stone-900">
        <T context="agent page current task card before the run has recorded a step">
          Run in progress
        </T>
      </p>
    );
  }
  const title = <span className="truncate">{headline}</span>;
  return (
    <p className="mt-0.5 flex min-w-0 items-center gap-2 text-sm font-semibold text-stone-900">
      {task?.id ? (
        <Link
          to="/tasks/$taskId"
          params={{ taskId: task.id }}
          className="min-w-0 truncate underline-offset-2 hover:underline"
        >
          {title}
        </Link>
      ) : (
        title
      )}
    </p>
  );
}

function InstructionBrief({
  instruction,
}: {
  instruction: TrajectoryInstruction;
}) {
  return (
    <div className="mt-2 max-h-72 overflow-y-auto rounded-md border border-border bg-stone-50 px-3 py-2">
      <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-stone-500">
        <T context="agent page current task, the brief given to the operator doing the work">
          Instruction to <Var>{instruction.operatorName}</Var>
        </T>
      </div>
      <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-stone-800">
        {instruction.text}
      </p>
    </div>
  );
}

function PlanLine({ plan }: { plan: TrajectoryPlan }) {
  return (
    <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-stone-500">
        <T context="agent page current task, plan row label">Plan</T>
      </span>
      {plan.current ? (
        <span className="min-w-0 truncate text-foreground">
          {plan.current.label}
        </span>
      ) : plan.isAuthored ? (
        <T context="agent page current task, how many procedure steps have been recorded">
          <Num>{plan.items.length}</Num> recorded procedure steps
        </T>
      ) : (
        <T context="agent page current task, how many task-loop phases have run">
          <Num>{plan.items.length}</Num> phases
        </T>
      )}
      {plan.done > 0 && (
        <span className="tabular-nums text-emerald-700">
          <T context="agent page current task, count of finished plan steps">
            <Num>{plan.done}</Num> done
          </T>
        </span>
      )}
      {plan.failed > 0 && (
        <span className="tabular-nums text-red-600">
          <T context="agent page current task, count of failed plan steps">
            <Num>{plan.failed}</Num> failed
          </T>
        </span>
      )}
    </p>
  );
}

function SignalCluster({ signals }: { signals: TrajectorySignals }) {
  const hasElapsed = signals.elapsedMs != null;
  const hasTokens = signals.tokens > 0;
  const hasFailed = signals.failed > 0;
  if (!hasElapsed && !hasTokens && !hasFailed) return null;
  return (
    <p className="flex shrink-0 flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-[11px] tabular-nums text-muted-foreground">
      {hasFailed && (
        <span className="flex items-center gap-1 text-red-600">
          <WarningIcon className="h-3 w-3 shrink-0" />
          <T context="agent page current task, count of failed trace spans">
            <Num>{signals.failed}</Num> failed
          </T>
        </span>
      )}
      {hasElapsed && signals.elapsedMs != null && (
        <span>{fmtMs(signals.elapsedMs)}</span>
      )}
      {hasTokens && (
        <T context="agent page current task, total tokens used by the run">
          <Var>{fmtTokens(signals.tokens)}</Var> tokens
        </T>
      )}
    </p>
  );
}
